import { authMe, type MeResponse } from '@codex/contracts';
import { forgetMe, rememberMe } from '../shell/session.js';
import { ApiError, api } from './client.js';

/**
 * Set by the server (`apps/server/src/auth/session.ts`'s `getAuth`) only when a session cookie
 * was present but resolved to no live session — never for a visit that had no cookie at all.
 */
const SESSION_ENDED_COOKIE = 'codex_session_ended';

/** True, and clears itself, only when the server marked the session we just lost as expired. */
function consumeSessionEndedMarker(): boolean {
  const present = document.cookie
    .split('; ')
    .some((entry) => entry === `${SESSION_ENDED_COOKIE}=1`);
  if (present) {
    document.cookie = `${SESSION_ENDED_COOKIE}=; Max-Age=0; Path=/; SameSite=Strict`;
  }
  return present;
}

/**
 * Where an unauthenticated visit to `pathname` should go: the login page, carrying `next` so it
 * can return here, and `reason=expired` only when the session we just lost had actually expired
 * (as opposed to this visitor never having signed in at all).
 */
export function loginRedirectUrl(pathname: string): string {
  const next = encodeURIComponent(pathname);
  const reason = consumeSessionEndedMarker() ? '&reason=expired' : '';
  return `/login?next=${next}${reason}`;
}

/**
 * For a route's `beforeLoad` (brief deliverable 14): resolves the signed-in session's
 * `MeResponse`, or navigates to `/login?next=<path>` on a 401 and never resolves, so the guarded
 * route is never rendered without a session. Any other failure (a 500, the server unreachable)
 * rejects, which shows the route's `errorComponent`.
 *
 * Wiring, as `apps/web/src/routes/index.tsx` does it: `beforeLoad: async ({ location }) => ({ me:
 * await requireSession(location.pathname) })`, with `pendingComponent: SessionPending`,
 * `pendingMs: 0` and `errorComponent: SessionError` (from `./SessionStates.tsx`). The guarded
 * page reads `me` from the route context and never calls `auth.me` itself.
 */
export function requireSession(pathname: string): Promise<MeResponse> {
  return api(authMe).then(
    (me) => {
      rememberMe(me);
      return me;
    },
    (error: unknown) => {
      if (error instanceof ApiError && error.status === 401) {
        forgetMe();
        window.location.assign(loginRedirectUrl(pathname));
        return new Promise<never>(() => {});
      }
      throw error;
    },
  );
}
